import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { StarRating } from "./StarRating";
import { ContactTransporterDialog } from "./ContactTransporterDialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ShieldCheck, Truck, MapPin, Star, Award, ArrowRight, UserPlus } from "lucide-react";

function FeaturedCard({ t }) {
  return (
    <div className={`border bg-white p-5 flex flex-col ${t.featured ? "border-accent border-2" : "border-border"}`} data-testid={`featured-card-${t.transporter_id}`}>
      <div className="flex items-start justify-between gap-2">
        <Truck className="h-5 w-5 text-primary" />
        <div className="flex gap-1.5">
          {t.featured && <Badge className="rounded-none bg-accent text-foreground text-[9px] uppercase tracking-wider"><Award className="h-3 w-3 mr-0.5" /> Featured</Badge>}
          {t.documents_verified && <Badge className="rounded-none bg-primary text-white text-[9px] uppercase tracking-wider"><ShieldCheck className="h-3 w-3 mr-0.5" /> Verified</Badge>}
        </div>
      </div>
      <Link to={`/transporter/${t.transporter_id}`} className="mt-3">
        <h3 className="font-display font-bold text-lg tracking-tight hover:text-primary">{t.company_name || t.name}</h3>
      </Link>
      <div className="flex items-center gap-2 mt-1" data-testid={`featured-rating-${t.transporter_id}`}>
        {t.average_rating != null ? (
          <><StarRating value={t.average_rating} size="h-4 w-4" /><span className="text-sm font-medium">{t.average_rating}</span><span className="text-xs text-muted-foreground">· {t.reviews_count} reviews</span></>
        ) : <span className="text-xs text-muted-foreground flex items-center gap-1"><Star className="h-3.5 w-3.5" /> No reviews yet</span>}
      </div>
      <div className="text-xs text-muted-foreground mt-1">{t.completed_jobs} completed jobs</div>
      <div className="space-y-1 mt-3 text-sm flex-1">
        {t.vehicle_type && <div className="flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-muted-foreground" /> {t.vehicle_type}</div>}
        {(t.service_areas || t.country) && <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {[t.service_areas, t.country].filter(Boolean).join(" · ")}</div>}
        {t.bio && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{t.bio}</p>}
      </div>
      <div className="flex gap-2 mt-4">
        <ContactTransporterDialog transporterId={t.transporter_id} transporterName={t.company_name || t.name}
          trigger={<Button className="rounded-none bg-primary hover:bg-primary/90 flex-1" data-testid={`featured-quote-${t.transporter_id}`}>Request Quote</Button>} />
        <Link to={`/transporter/${t.transporter_id}`} className="flex-1">
          <Button variant="outline" className="rounded-none w-full border-foreground" data-testid={`featured-profile-${t.transporter_id}`}>View Profile</Button>
        </Link>
      </div>
    </div>
  );
}

export const FeaturedTransporters = ({ limit = 6, dark = false }) => {
  const [list, setList] = useState(null);

  useEffect(() => {
    api.get("/transporters/featured", { params: { limit } }).then((r) => setList(r.data)).catch(() => setList([]));
  }, [limit]);

  if (list === null) return null;

  return (
    <section className={`py-16 ${dark ? "bg-foreground text-white" : "bg-muted"}`} data-testid="featured-transporters">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8">
          <div>
            <span className="label-caps text-accent">Verified network</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-1">Featured transporters</h2>
            <p className={`mt-2 max-w-xl text-sm ${dark ? "text-white/70" : "text-muted-foreground"}`}>Admin-verified, top-rated transporters ready to quote on your jobs.</p>
          </div>
          <Link to="/find-transporter" data-testid="featured-find-all">
            <Button variant="outline" className={`rounded-none ${dark ? "border-white text-white hover:bg-white hover:text-foreground" : "border-foreground"}`}>
              Find a Transporter <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>

        {list.length === 0 ? (
          <div className={`border border-dashed p-16 text-center ${dark ? "border-white/30 text-white/60" : "border-border text-muted-foreground"}`} data-testid="featured-empty">
            No featured transporters yet. Verified transporters will appear here soon.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="featured-grid">
            {list.map((t) => <FeaturedCard key={t.transporter_id} t={t} />)}
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-10">
          <Link to="/find-transporter" data-testid="featured-cta-find"><Button className="rounded-none bg-primary hover:bg-primary/90 h-11 px-6">Find a Transporter</Button></Link>
          <Link to="/register" data-testid="featured-cta-register">
            <Button variant="outline" className={`rounded-none h-11 px-6 ${dark ? "border-white text-white hover:bg-white hover:text-foreground" : "border-foreground"}`}>
              <UserPlus className="h-4 w-4 mr-1" /> Register as Transporter / Sprinter Driver
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
